// Package imports:
import React, { useState } from 'react';
// Component imports:
import DisplayBox from '../../../ui-elements/DisplayBox/DisplayBox';
import Input from '../../../ui-elements/Input/Input';
import Radio from '../../../ui-elements/Radio/Radio';
import Checkbox from '../../../ui-elements/Input/Checkbox';
import Button from '../../../ui-elements/Button/Button';
import DropSelectSearch from '../../../ui-elements/DropSelect/DropSelectSearch/DropSelectSearch';
import { CountryList } from '../../../assets/staticData/CountryList';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * IndividualKYCExample
 *
 * A static example/preview component that demonstrates the layout and structure
 * of an Individual KYC (Know Your Customer) due diligence questionnaire.
 *
 * This is NOT a fully interactive form — text inputs are disabled.
 * However, radio button selections ARE functional and toggle the visibility
 * of conditional fields and sub-questions to demonstrate the intended UX flow.
 *
 * The Radio component works by comparing its `value` prop against `defaultValue`.
 * When `value === defaultValue`, the radio appears checked. The `onChange` callback
 * fires on mouse down and returns the selected value, which we store in state
 * and pass back as `defaultValue` to all radios in the same group.
 *
 * Questions covered:
 *  1. Legal address (always visible, pre-filled input)
 *  2. Employment status (dropdown with conditional text input when "Annað")
 *  3. Tax residency (conditional country dropdown + TIN when "Nei")
 *  4. Cash-intensive business (conditional sub-questions when "Já")
 *  5. Purpose of intended business relationship (always visible input)
 *  6. Beneficial owner of funds (conditional text input when "Nei")
 *  7. Source of funds (conditional text input when "Annað")
 *  8. PEP status (conditional detail fields when "Já", plus sub-questions 8b/8c)
 */
const IndividualKYCExample: React.FC = () => {
    // =====================================================================
    // STATE — Radio / Dropdown selections
    // Each question with conditional content gets its own state variable.
    // The value stored here is the string that matches the Radio `value` prop.
    // An empty string "" means no selection has been made yet.
    //
    // For yes/no questions we use "true"/"false" strings to match Radio values.
    // For multi-option questions (like Q7) we use the option identifiers.
    // =====================================================================

    /** Q2: Employment status (dropdown with conditional text input when "Annað") */
    const [q2, setQ2] = useState<string>("");

    /** Q3: Tax domicile in Iceland? "false" reveals country dropdown + TIN field */
    const [q3, setQ3] = useState<string>("");

    /** Q4: Cash-intensive business? "true" reveals sub-questions */
    const [q4, setQ4] = useState<string>("");

    /** Q4a: Cash percentage of transactions (sub-question, no conditional content) */
    const [q4a, setQ4a] = useState<string>("");

    /** Q6: Beneficial owner of funds? "false" reveals text input */
    const [q6, setQ6] = useState<string>("");

    /** Q7: Source of funds. "opt4" (Annað/Other) reveals text input */
    const [q7, setQ7] = useState<string>("");

    /** Q8: PEP status? "true" reveals position/relationship/deposits fields */
    const [q8, setQ8] = useState<string>("");

    /** Q8b: Regular foreign payments? "true" reveals purpose text input */
    const [q8b, setQ8b] = useState<string>("");

    /** Q8c: Regular cash usage with XXXX? "true" reveals purpose text input */
    const [q8c, setQ8c] = useState<string>("");

    // =====================================================================
    // STATE — Text input values
    // Each text input gets its own state variable so the component is fully
    // controlled and values can be collected on submit.
    // =====================================================================

    /** Q1: Legal address */
    const [q1Value, setQ1Value] = useState<string>("Kelduvogur 20, Reykjavík");

    /** Q2 conditional: Employment status explanation (when "Annað" selected) */
    const [q2TextValue, setQ2TextValue] = useState<string>("");

    /** Q3 conditional: Tax Identification Number (when "Nei" selected) */
    const [q3TinValue, setQ3TinValue] = useState<string>("");

    /** Q4 conditional: Cash usage explanation */
    const [q4TextValue, setQ4TextValue] = useState<string>("");

    /** Q5: Purpose of business relationship */
    const [q5Value, setQ5Value] = useState<string>("");

    /** Q6 conditional: Beneficial owner explanation (when "Nei" selected) */
    const [q6TextValue, setQ6TextValue] = useState<string>("");

    /** Q7 conditional: Source of funds explanation (when "Annað" selected) */
    const [q7TextValue, setQ7TextValue] = useState<string>("");

    /** Q8 conditional: PEP position */
    const [q8PositionValue, setQ8PositionValue] = useState<string>("");

    /** Q8 conditional: PEP relationship */
    const [q8RelationshipValue, setQ8RelationshipValue] = useState<string>("");

    /** Q8 conditional: PEP deposits source */
    const [q8DepositsValue, setQ8DepositsValue] = useState<string>("");

    /** Q8b conditional: Foreign payments purpose */
    const [q8bTextValue, setQ8bTextValue] = useState<string>("");

    /** Q8c conditional: Cash usage purpose */
    const [q8cTextValue, setQ8cTextValue] = useState<string>("");

    /** Checkbox state for the final confirmation */
    const [checkboxStatus, setCheckboxStatus] = useState<boolean>(false);

    return (
        <div className="kyc-example">
            <h1>Áreiðanleikakönnun</h1>

            {/* Individual info header showing basic personal details */}
            <div className="KCL_company-title">
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Nafn</span>
                        <span className="value">Kelda Kóðadóttir</span>
                    </div>
                </div>
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Kennitala</span>
                        <span className="value">091079-1239</span>
                    </div>
                </div>
                <div className="info-col">
                    <div className="info-item">
                        <span className="title">Póstfang</span>
                        <span className="value">Kelduvogur 20, Reykjavík</span>
                    </div>
                </div>
            </div>

            <DisplayBox displayBoxSize="xl" grayBackground>
                <ol className="kyc-questions">
                    {/* ============================================================
                        QUESTION 1: Legal Address
                        Asks the user to provide their legal address.
                        The input is pre-filled and disabled as this is an
                        example/preview component.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Hvert er lögheimili þitt?
                        </p>
                        <p className="paragraph-small">
                            Vinsamlega tilgreinið heimilisfang og póstnúmer.
                        </p>
                        <Input
                            label="Heimilisfang"
                            inputSize="lg"
                            value={q1Value}
                            onChange={(e) => setQ1Value(e.target.value)}
                        />
                    </li>

                    {/* ============================================================
                        QUESTION 2: Employment Status
                        Asks the user their current employment status via a
                        dropdown select. When "Annað" (Other) is selected in
                        the dropdown, a text input appears for further explanation.
                        
                        Note: The dropdown and conditional field are shown as
                        static examples here since this is a preview component.
                        The dropdown is pre-set to "Annað" to demonstrate the
                        conditional text input.
                    ============================================================ */}
                    <li className='individual-question2'>
                        <p className="paragraph--bold">
                            Hver er staða þín á vinnumarkaði?
                        </p>
                        <DropSelectSearch
                            options={[
                                { value: 'launþegi', label: 'Launþegi í einkageira' },
                                { value: 'sjálfstætt_starfandi', label: 'Sjálfstætt starfandi' },
                                { value: 'opinber_starfsmaður', label: 'Opinber starfsmaður' },
                                { value: 'námsmadur', label: 'Námsmaður' },
                                { value: 'utan_vinnumarkadar', label: 'Utan vinnumarkaðar' },
                                { value: 'annad', label: 'Annað' },
                            ]}
                            onChange={(e: string) => setQ2(e)}
                            label="Staða á vinnumarkaði"
                            size='lg'
                        />
                        {/* 
                            Conditional text input: In the real form this would appear
                            when "Annað" is selected in the dropdown above.
                            Shown here as always visible for demonstration purposes.
                        */}
                        {
                            q2 === "annad" &&
                            <Input
                                className='inputThatNeedsMargin'
                                label="Vinsamlegast útskýrið nánar"
                                inputSize="lg"
                                value={q2TextValue}
                                onChange={(e) => setQ2TextValue(e.target.value)}
                            />
                        }
                    </li>

                    {/* ============================================================
                        QUESTION 3: Tax Residency
                        Asks whether the individual's tax domicile is in Iceland.
                        If "Nei" (No) is selected, a country dropdown and a TIN
                        (Tax Identification Number) input appear so the user can
                        specify which country and provide their foreign tax ID.
                        An add button allows adding multiple country/TIN entries.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Er skattaleg heimilisfesti þín á Íslandi?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q3-yes"
                                        name="q3"
                                        value="true"
                                        defaultValue={q3}
                                        onChange={() => setQ3("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q3-no"
                                        name="q3"
                                        value="false"
                                        defaultValue={q3}
                                        onChange={() => setQ3("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional fields: only visible when "Nei" is selected.
                            Shows a country dropdown and TIN input side by side,
                            plus an add button for multiple entries. */}
                        {q3 === "false" && (
                            <div className="conditional-field">
                                <div className="input-row">
                                    <DropSelectSearch
                                        options={CountryList}
                                        label="Tilgreinið land"
                                        size='lg'
                                    />
                                    <div className="input-with-action">
                                        <Input
                                            label="Skattkennitala (TIN)"
                                            inputSize="lg"
                                            value={q3TinValue}
                                            onChange={(e) => setQ3TinValue(e.target.value)}
                                        />
                                        {/* Add button to allow multiple country/TIN entries */}
                                        <i className="fa-solid fa-circle-plus add-icon" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 4: Cash-Intensive Business
                        Asks whether the individual deals with significant amounts
                        of cash. When "Já" (Yes) is selected, a sub-question
                        appears asking what percentage of their transactions
                        involve cash, plus a text input for further explanation.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Stundar þú viðskipti með mikið reiðufé?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q4-yes"
                                        name="q4"
                                        value="true"
                                        defaultValue={q4}
                                        onChange={() => setQ4("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q4-no"
                                        name="q4"
                                        value="false"
                                        defaultValue={q4}
                                        onChange={() => setQ4("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>

                        {/* Sub-questions only appear when "Já" is selected */}
                        {q4 === "true" && (
                            <div className="sub-question">
                                {/* Sub-question 4a: What percentage of transactions involve cash */}
                                <p className="paragraph-small--bold">
                                    Hversu stór hluti viðskipta þinna felur í sér reiðufé?
                                </p>
                                <div className="radios-group">
                                    <ul>
                                        <li>
                                            <Radio
                                                id="q4a-opt1"
                                                name="q4a"
                                                value="opt1"
                                                defaultValue={q4a}
                                                onChange={() => setQ4a("opt1")}
                                            >
                                                Minna en 5%
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q4a-opt2"
                                                name="q4a"
                                                value="opt2"
                                                defaultValue={q4a}
                                                onChange={() => setQ4a("opt2")}
                                            >
                                                5 til 20%
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q4a-opt3"
                                                name="q4a"
                                                value="opt3"
                                                defaultValue={q4a}
                                                onChange={() => setQ4a("opt3")}
                                            >
                                                Meira en 20%
                                            </Radio>
                                        </li>
                                    </ul>
                                </div>
                                {/* Text input for further explanation about cash usage */}
                                <Input
                                    label="Vinsamlegast útskýrið nánar"
                                    inputSize="lg"
                                    value={q4TextValue}
                                    onChange={(e) => setQ4TextValue(e.target.value)}
                                />
                            </div>
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 5: Purpose of Intended Business
                        Asks the user to describe the purpose of the intended
                        business relationship — i.e., what service or solution
                        they intend to use from XXXX.
                        This field is always visible (no conditional logic).
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Hver er tilgangur fyrirhugaðra viðskipta, það er hvaða þjónustu eða lausn hyggst þú nýta hjá XXXX?
                        </p>
                        <Input
                            className='inputThatNeedsMargin'
                            label="Tilgangur viðskiptasambands"
                            inputSize="lg"
                            value={q5Value}
                            onChange={(e) => setQ5Value(e.target.value)}
                        />
                    </li>

                    {/* ============================================================
                        QUESTION 6: Beneficial Owner of Funds
                        Asks whether the individual is the true/beneficial owner
                        of the funds used in the business relationship.
                        When "Nei" (No) is selected, a text input appears asking
                        the user to identify who the actual beneficial owner is.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Ert þú raunverulegur eigandi þeirra fjármuna sem notaðir eru í viðskiptasambandinu?
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q6-yes"
                                        name="q6"
                                        value="true"
                                        defaultValue={q6}
                                        onChange={() => setQ6("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q6-no"
                                        name="q6"
                                        value="false"
                                        defaultValue={q6}
                                        onChange={() => setQ6("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional text input: only visible when "Nei" is selected */}
                        {q6 === "false" && (
                            <Input
                                className='inputThatNeedsMargin'
                                label="Vinsamlegast tilgreindu hver raunverulegur eigandi fjármuna er"
                                inputSize="lg"
                                value={q6TextValue}
                                onChange={(e) => setQ6TextValue(e.target.value)}
                            />
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 7: Source of Funds
                        Asks the user to specify the origin of funds used for
                        transactions with the service provider (XXXX).
                        Options include capital income/wages/dividends, operating
                        revenue, loans/investment, or "Other".
                        When "Annað" (Other / opt4) is selected, a text input appears.
                    ============================================================ */}
                    <li>
                        <p className="paragraph--bold">
                            Tilgreindu uppruna þeirra fjármuna sem notaðir eru til viðskipta við XXXX.
                        </p>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q7-opt1"
                                        name="q7"
                                        value="opt1"
                                        defaultValue={q7}
                                        onChange={() => setQ7("opt1")}
                                    >
                                        Fjármagnstekjuskattur, laun, arður
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q7-opt2"
                                        name="q7"
                                        value="opt2"
                                        defaultValue={q7}
                                        onChange={() => setQ7("opt2")}
                                    >
                                        Rekstrartekjur
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q7-opt3"
                                        name="q7"
                                        value="opt3"
                                        defaultValue={q7}
                                        onChange={() => setQ7("opt3")}
                                    >
                                        Lánsfé eða fjárfesting
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q7-opt4"
                                        name="q7"
                                        value="opt4"
                                        defaultValue={q7}
                                        onChange={() => setQ7("opt4")}
                                    >
                                        Annað
                                    </Radio>
                                </li>
                            </ul>
                        </div>
                        {/* Conditional text input: only visible when "Annað" (opt4) is selected */}
                        {q7 === "opt4" && (
                            <Input
                                className='inputThatNeedsMargin'
                                label="Vinsamlegast gerðu grein fyrir uppruna fjármuna"
                                inputSize="lg"
                                value={q7TextValue}
                                onChange={(e) => setQ7TextValue(e.target.value)}
                            />
                        )}
                    </li>

                    {/* ============================================================
                        QUESTION 8: PEP (Politically Exposed Person) Status
                        A multi-part question that asks:
                          a) Whether the user, a close family member, or associate
                             holds or has held a high-ranking public position
                          b) If yes: which position, what relationship, and source
                             of account deposits
                          c) Whether the user regularly receives or sends foreign
                             payments (to/from Iceland)
                          d) Whether the user regularly uses cash in their
                             transactions with XXXX

                        Sub-questions 8b and 8c become visible once Q8 has any
                        selection, since they are separate follow-up questions
                        within the same numbered item. Their own text inputs
                        only show when their respective "Já" is selected.
                    ============================================================ */}
                    <li>
                        {/* Main PEP question with info icon */}
                        <div className="question-with-info">
                            <p className="paragraph--bold">
                                Stjórnmálaleg tengsl: Ert þú, náinn fjölskyldumeðlimur eða samstarfsmaður háttsettur í opinberri þjónustu?
                            </p>
                            <Tooltip
                                tooltipClassName="viewCompanyAnswers-tooltip"
                                noUnderline
                                tooltip={<div className="tooltip-text">
                                    Til einstaklinga í áhættuhópi vegna stjórnmálalegra tengsla teljast þeir einstaklingar, innlendir og erlendir, sem eru eða hafa verið háttsettir í opinberri þjónustu, ásamt nánustu fjölskyldu þeirra og nánum samstarfsmönnum. Seðlabanki Íslands heldur úti lista yfir starfsheiti sem teljast til háttsettra opinberra starfa.
                                </div>}
                                text={<FontAwesomeIcon icon={faInfoCircle} />}
                            />
                        </div>
                        <div className="radios-group">
                            <ul>
                                <li>
                                    <Radio
                                        id="q8-yes"
                                        name="q8"
                                        value="true"
                                        defaultValue={q8}
                                        onChange={() => setQ8("true")}
                                    >
                                        Já
                                    </Radio>
                                </li>
                                <li>
                                    <Radio
                                        id="q8-no"
                                        name="q8"
                                        value="false"
                                        defaultValue={q8}
                                        onChange={() => setQ8("false")}
                                    >
                                        Nei
                                    </Radio>
                                </li>
                            </ul>
                        </div>

                        {/* Conditional fields when PEP = "Já": position, relationship, deposits source */}
                        {q8 === "true" && (
                            <div className="conditional-field question8-I">
                                <Input
                                    label="Hvaða háttsetta starf/störf?"
                                    inputSize="lg"
                                    value={q8PositionValue}
                                    onChange={(e) => setQ8PositionValue(e.target.value)}
                                />
                                <Input
                                    label="Hver eru tengslin?"
                                    inputSize="lg"
                                    value={q8RelationshipValue}
                                    onChange={(e) => setQ8RelationshipValue(e.target.value)}
                                />
                                <Input
                                    label="Hvaðan berast innborganir inná reikninga þína?"
                                    inputSize="lg"
                                    value={q8DepositsValue}
                                    onChange={(e) => setQ8DepositsValue(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Sub-question 8b: Regular foreign payments
                            Visible once Q8 has been answered (regardless of yes/no),
                            since this is a separate follow-up within the same question group */}
                        {q8 === "true" && (
                            <div className="sub-question">
                                <p className="paragraph-small--bold">
                                    Móttekur þú eða sendir reglulega út erlendar greiðslur (til og frá Íslandi)?
                                </p>
                                <div className="radios-group">
                                    <ul>
                                        <li>
                                            <Radio
                                                id="q8b-yes"
                                                name="q8b"
                                                value="true"
                                                defaultValue={q8b}
                                                onChange={() => setQ8b("true")}
                                            >
                                                Já
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q8b-no"
                                                name="q8b"
                                                value="false"
                                                defaultValue={q8b}
                                                onChange={() => setQ8b("false")}
                                            >
                                                Nei
                                            </Radio>
                                        </li>
                                    </ul>
                                </div>
                                {/* Purpose text input: only visible when Q8b = "Já" */}
                                {q8b === "true" && (
                                    <Input
                                        label="Í hvaða tilgangi?"
                                        inputSize="lg"
                                        value={q8bTextValue}
                                        onChange={(e) => setQ8bTextValue(e.target.value)}
                                    />
                                )}
                            </div>
                        )}

                        {/* Sub-question 8c: Regular cash usage with XXXX
                            Also visible once Q8 has been answered */}
                        {q8 === "true" && (
                            <div className="sub-question">
                                <p className="paragraph-small--bold">
                                    Notar þú reglulega reiðufé í þínum viðskiptum við XXXX?
                                </p>
                                <div className="radios-group">
                                    <ul>
                                        <li>
                                            <Radio
                                                id="q8c-yes"
                                                name="q8c"
                                                value="true"
                                                defaultValue={q8c}
                                                onChange={() => setQ8c("true")}
                                            >
                                                Já
                                            </Radio>
                                        </li>
                                        <li>
                                            <Radio
                                                id="q8c-no"
                                                name="q8c"
                                                value="false"
                                                defaultValue={q8c}
                                                onChange={() => setQ8c("false")}
                                            >
                                                Nei
                                            </Radio>
                                        </li>
                                    </ul>
                                </div>
                                {/* Purpose text input: only visible when Q8c = "Já" */}
                                {q8c === "true" && (
                                    <Input
                                        label="Í hvaða tilgangi?"
                                        inputSize="lg"
                                        value={q8cTextValue}
                                        onChange={(e) => setQ8cTextValue(e.target.value)}
                                    />
                                )}
                            </div>
                        )}
                    </li>
                </ol>

                {/* ============================================================
                    CONFIRMATION SECTION
                    The user must check a checkbox confirming that the
                    information provided is correct to the best of their
                    knowledge, and that they commit to notifying XXXX
                    of any changes as soon as possible.
                ============================================================ */}
                <div className="confirm-info">
                    <p className="paragraph--bold">
                        Staðfesting á réttum upplýsingum
                    </p>
                    <p className="paragraph-small">
                        Ég staðfesti að þær upplýsingar sem ég hef veitt hér að ofan séu réttar eftir minni bestu vitund og skuldbind mig til að tilkynna XXXX skriflega um breytingar á þeim eins fljótt og mögulegt er.
                    </p>
                    <Checkbox
                        name="kyc-checkbox"
                        id={`kyc-checkbox-individual`}
                        label={"Ég samþykki"}
                        value={(checkboxStatus || false).toString()}
                        onClick={() =>
                            setCheckboxStatus(!checkboxStatus)
                        }
                    />
                </div>

                {/* Cancel and Submit buttons */}
                <div className="actions">
                    <Button
                        disabled
                        buttonType="secondary"
                        size="lg"
                    >
                        Hætta við
                    </Button>
                    <Button
                        disabled
                        buttonType="primary"
                        size="lg"
                    >
                        Staðfesta
                    </Button>
                </div>
            </DisplayBox>
        </div>
    );
}

export default IndividualKYCExample;